import { FC, useLayoutEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import Cookies from 'js-cookie'
import './Charts.scss';

type ClusteredColumnLineSettingsProps = {
    label: string,
    fieldName: string
};
 
type ClusteredColumnChartProps = {
    chartName: string,
    columnName: string,
    lineSettings?: ClusteredColumnLineSettingsProps,
    data: any[],
    columnsSettings: ClusteredColumnLineSettingsProps[],
    titleX?: string,
    titleY?: string,
    handleSelect?: (a: any) => void
    percentY?: boolean,
    hideAxisX?: boolean,
    height?: number
};

const ClusteredColumnChart: FC<ClusteredColumnChartProps> = ({ chartName, columnName, data, columnsSettings, lineSettings, titleX, titleY, handleSelect, percentY, hideAxisX, height }) => {

    const chartId = chartName + Date.now().toString().substr(-4)
    useLayoutEffect(() => {
        am5.ready(function () {
            am5.addLicense('AM5M326788873');

            /* Chart code */
            // Create root element
            // https://www.amcharts.com/docs/v5/getting-started/#Root_element
            let root = am5.Root.new(chartId);

            // Default colors, if more colors are needed then add it to the array below
            const colors = [
                ((Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x487292)),
                ((Cookies.get('darkMode') === "true") ? am5.color(0x4e83b1) : am5.color(0x8ccbe9)),
            ]


            // Set themes
            // https://www.amcharts.com/docs/v5/concepts/themes/
            root.setThemes([
                am5themes_Animated.new(root)
            ]);


            // Create chart
            // https://www.amcharts.com/docs/v5/charts/xy-chart/
            let chart = root.container.children.push(am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: "none",
                wheelY: "none",
                layout: root.verticalLayout
            }));


            // Add legend
            // https://www.amcharts.com/docs/v5/charts/xy-chart/legend-xy-series/
            let legend = chart.children.unshift(
                am5.Legend.new(root, {
                    centerX: am5.p50,
                    x: am5.p50,
                    y: 10
                })
            );

            legend.labels.template.setAll({
                fontSize: Number(Cookies.get('globalFontSize'))*0.85, //12px,
                fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
            });


            // Create axes
            // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
            let xRenderer = am5xy.AxisRendererX.new(root, {
                cellStartLocation: 0.1,
                cellEndLocation: 0.9,
            });

            xRenderer.labels.template.setAll({
                oversizedBehavior: "wrap",
                maxWidth: 130, fontSize: 12,
                fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
            });

            const xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: columnName,
                    renderer: xRenderer,
                    tooltip: am5.Tooltip.new(root, {})
                }),
            );

            xRenderer.grid.template.setAll({
                location: 1
            })

            xAxis.children.push(
                am5.Label.new(root, {
                    text: titleX,
                    fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
                    fontWeight: "600",
                    fontStyle: "italic",
                    x: am5.p50,
                    centerX: am5.p50
                })
            );

            xAxis.data.setAll(data);

            if (hideAxisX) {
                xAxis.hide();
            }
            
            const yRenderer = am5xy.AxisRendererY.new(root, { minGridDistance: 20 });

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    min: 0,
                    numberFormat: percentY ? "#'%'" : "#",
                    renderer: yRenderer,
                }),
            );

            yAxis.children.unshift(
                am5.Label.new(root, {
                    rotation: -90,
                    fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
                    fontWeight: "600",
                    fontStyle: "italic",
                    text: titleY,
                    y: am5.p50,
                    centerX: am5.p50
                })
            );

            yRenderer.labels.template.setAll({
                fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
                fontSize: 12,
            });

            // Add series
            // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
            function makeSeries(name: string, fieldName: string, color: am5.Color) {
                let series = chart.series.push(am5xy.ColumnSeries.new(root, {
                    name: name,
                    xAxis: xAxis,
                    yAxis: yAxis,
                    fill: color,
                    valueYField: fieldName,
                    categoryXField: columnName,
                    tooltip: am5.Tooltip.new(root, {
                        labelText: percentY ? "{name}:\n[bold]{valueY}%[/]" : "{name}:\n[bold]{valueY}[/]",
                    }),
                }));

                series.columns.template.setAll({
                    tooltipText: "{name}",
                    width: am5.percent(90),
                    tooltipY: 0,
                    strokeOpacity: 0
                });

                series.data.setAll(data);

                // Make stuff animate on load
                // https://www.amcharts.com/docs/v5/concepts/animations/
                series.appear();

                series.bullets.push(function () {
                    return am5.Bullet.new(root, {
                        locationY: 1,
                        sprite: am5.Label.new(root, {
                        })
                    });
                });
                
                // Rounded corners for columns
                series.columns.template.setAll({
                    cornerRadiusTR: 5,
                    cornerRadiusTL: 5,
                });

                series.columns.template.events.on("click", function (ev: any) {
                    if (handleSelect) {
                        handleSelect(ev.target.dataItem.dataContext);
                    }
                });                

                legend.data.push(series);
            }

            // Prepare series data using columns settings
            columnsSettings && columnsSettings.forEach((value: ClusteredColumnLineSettingsProps, index: number) => {
                makeSeries(value.label, value.fieldName, colors[index]);
            })

            // Add line chart if available
            if (lineSettings) {
                let lineSeries = chart.series.push(
                    am5xy.LineSeries.new(root, {
                        name: lineSettings.label,
                        xAxis: xAxis,
                        yAxis: yAxis,
                        valueYField: lineSettings.fieldName,
                        categoryXField: columnName,
                    })
                );

                lineSeries.strokes.template.setAll({
                    strokeOpacity: 0,
                    templateField: "strokeSettings"
                });


                lineSeries.data.setAll(data);

                lineSeries.bullets.push(function () {
                    return am5.Bullet.new(root, {
                        sprite: am5.Circle.new(root, {
                            strokeWidth: 3,
                            stroke: am5.color(0x003b5c),
                            radius: 5,
                            fill: am5.color(0xd9d9d9),
                            tooltipText: percentY ? "{name}:\n[bold]{valueY}%[/]" : "{name}:\n[bold]{valueY}[/]",
                        })
                    });
                });

                // By default on load hide line series
                lineSeries.hide();

                legend.data.push(lineSeries);
            }
            
            // Make stuff animate on load
            // https://www.amcharts.com/docs/v5/concepts/animations/
            chart.appear(1000, 100);
        });
    }, []);// eslint-disable-line

    return <div id={chartId} className="chart-container" style={{ height: height || 200 }} />;
};

export default ClusteredColumnChart;
